import React, { SyntheticEvent, useCallback, useEffect, useState } from 'react'
import * as Yup from 'yup'
import { FieldArray } from 'formik'
import { useTranslation } from 'react-i18next'
import { useDebounce } from 'use-debounce'

import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import DeleteIcon from '@mui/icons-material/Delete'

import { Locales } from '@carsys/enums/locales'
import { ISupportMessage, MessageTemplate, ShopModule } from '@cbgms/api/modules/messages'
import { enumToOptions } from '@cbgms/base/utils/helpers'
import { Autocomplete, BasicFormLayout, DateTimePicker, Dropdown, Form, TextField } from '@carsys/form'
import { enumToLanguageOptions, supportedLanguages } from '@cbgms/base/translate/locales'
import { getShop, listShopsAll } from 'app-support/shops/state/actions'
import { useDispatch } from 'react-redux'
import { Timing } from '@cbgms/base/utils/timing'
import useQuery from '@cbgms/base/http/use-query'
import { mapShopIdToLabel } from 'app-support/stats/utils'
import { query } from '@cbgms/components/list'
import { StatisticsShops } from '@cbgms/api'

interface Props {
  initialValues: ISupportMessage
  onSubmit: (formValues: ISupportMessage) => Promise<ISupportMessage>
  onSubmitSuccess: (formValues: ISupportMessage) => void
}
interface ShopOption {
  text: string
  key: string
  value: string
}

interface ShopResponse {
  Shop: {
    ID: string
    Name: string
    VisitorCity: string
  }
}

const MessageForm: React.FC<Props> = ({ initialValues, onSubmit, onSubmitSuccess }) => {
  const messageTemplateOptions = enumToOptions(MessageTemplate, 'EnumSystemMessageType')
  const shopModuleOptions = enumToOptions(ShopModule, 'EnumShopModule')
  const localeOptions = enumToLanguageOptions(supportedLanguages)
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [page, setPage] = useState(1)
  const [totalShops, setTotalShops] = useState(0)
  const [totalPage, setTotalPage] = useState(0)
  const [currentPage, setCurrentPage] = useState(0)
  const [ShopList, setShopList] = useState<ShopOption[]>([])
  const [queryString, setQueryString] = useState('')
  const [isSearchLoading, setIsSearchLoading] = useState(false)
  const [formValues, setFormValues] = useState(initialValues)
  const [isAppendingData, setIsAppendingData] = useState(false)

  const [searchQuery] = useDebounce(queryString, Timing.keyboardInput)
  const { isLoading, isFetching, data } = useQuery(['SUPPORT', 'SHOP_SEARCHES', searchQuery, page], async () => {
    const searchShopQuery = query<ShopResponse>()
      .contains(i => i.Shop.ID, queryString)
      .or()
      .contains(i => i.Shop.Name, queryString)
      .or()
      .contains(i => i.Shop.VisitorCity, queryString)

    const data = await dispatch(
      listShopsAll({
        perPage: 150,
        page,
        fields: 'Shop.UUID,Shop.Name,Shop.VisitorCity',
        search: `${searchShopQuery}`
      })
    )
    const mappedData = mapShopData(data.Data)
    if (data.Paging) {
      setCurrentPage(data.Paging.Page)
      setTotalShops(data.Paging.Total)
      setTotalPage(data.Paging.TotalPages)
    }

    setShopListByStatus(mappedData)
    setIsSearchLoading(false)
    return mappedData
  })
  const mapAPIData = async () => {
    if (Array.isArray(initialValues?.ShopIDs) && initialValues?.ShopIDs.length > 0) {
      initialValues?.ShopIDs.map(async shopId => {
        const data = await dispatch(getShop(shopId))
        if (data) {
          const mappedData = mapShopIdToLabel(data.Data)
          const values = formValues
          values.ShopIDs = mappedData.text
          setFormValues(values)
        }
      })
    }
  }
  const validationSchema = Yup.object().shape({
    PublishDate: Yup.date().required(),
    EndDate: Yup.date().required(),
    Message: Yup.string().required(),
    Title: Yup.string().required(),
    Template: Yup.mixed<MessageTemplate>().oneOf(Object.values(MessageTemplate)),
    TargetedModules: Yup.array(Yup.mixed<ShopModule>().oneOf(Object.values(ShopModule))),
    LocalizedMessages: Yup.array(
      Yup.object().shape({
        Title: Yup.string().required(),
        Message: Yup.string().required(),
        Locale: Yup.mixed<Locales>().oneOf(Object.values(Locales))
      })
    )
  })

  const mapShopData = (data: StatisticsShops[]): ShopOption[] => {
    return data.map((item: StatisticsShops) => {
      const mappedShopData = mapShopIdToLabel(item.Shop)
      return mappedShopData
    })
  }
  const handleSearchChange = (event: SyntheticEvent, value: string) => {
    event && event?.preventDefault()
    setIsSearchLoading(true)
    setIsAppendingData(false)
    setQueryString(value.trim())
    setPage(0)
  }
  const handleScroll = (event: React.SyntheticEvent) => {
    const listboxNode = event.currentTarget
    if (listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight) {
      if (!isSearchLoading && ShopList.length < totalShops && currentPage < totalPage) {
        setPage(prevPage => {
          setIsAppendingData(true)
          return prevPage + 1
        })
      }
    }
  }
  const setShopListByStatus = useCallback(
    (mappeddata: ShopOption[]) => {
      if (isAppendingData) {
        setShopList(prevList => [...prevList, ...mappeddata])
      } else {
        setShopList(mappeddata)
      }
    },
    [data]
  )

  useEffect(() => {
    if (initialValues?.ShopIDs.length > 0) {
      mapAPIData()
    }
  }, [initialValues])

  return (
    <Form initialValues={formValues} onSubmit={onSubmit} onSubmitSuccess={onSubmitSuccess} validationSchema={validationSchema}>
      {({ values }) => (
        <BasicFormLayout backButtonProps={{ to: () => '/messages' }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant='h5'>{t('messages.configuration')}</Typography>
            </Grid>
            <Grid item xs={3}>
              <DateTimePicker name='PublishDate' label={t('entities.systemmessage.publishdate')} required />
            </Grid>
            <Grid item xs={3}>
              <DateTimePicker name='EndDate' label={t('entities.systemmessage.enddate')} required />
            </Grid>
            <Grid item xs={3}>
              <Dropdown name='Template' label={t('entities.systemmessage.template')} options={messageTemplateOptions} />
            </Grid>
            <Grid item xs={3}>
              <Dropdown
                name='TargetedModules'
                label={t('entities.systemmessage.targetedmodules')}
                options={shopModuleOptions}
                multiple
                data-testid='targeted-modules'
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                disableClearable={queryString === ''}
                loading={isSearchLoading || isLoading || isFetching}
                label={t('entities.systemmessage.shop')}
                placeholder={t('entities.systemmessage.shop')}
                role='listbox'
                onInputChange={handleSearchChange}
                ListboxProps={{
                  onScroll: handleScroll
                }}
                getOptionLabel={option => option?.text || option}
                name='ShopIDs'
                disablePortal
                id='shop-list'
                options={ShopList}
                sx={{ width: '100%' }}
              />
              <Typography sx={{ marginTop: '5px' }} variant='body1'>
                {t('messages.shop_list_disclaimer')}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant='h5'>{t('messages.fallback_message')}</Typography>
              <Typography variant='body1'>{t('messages.fallback_message_explanation')}</Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField name='Title' label={t('entities.systemmessage.title')} required />
            </Grid>
            <Grid item xs={12}>
              <TextField name='Message' multiline label={t('entities.systemmessage.message')} required />
            </Grid>
            <Grid item xs={12}>
              <Divider variant='fullWidth' orientation='horizontal' />
            </Grid>
            <Grid item xs={12}>
              <Typography variant='h5'>{t('Messages.localized_messages')}</Typography>
            </Grid>
            <FieldArray name='LocalizedMessages'>
              {({ remove, push }) => (
                <Grid item container xs={12} spacing={3}>
                  {values?.LocalizedMessages?.map((_, index) => (
                    <Grid item key={index} container xs={12} spacing={2}>
                      <Grid item xs={2}>
                        <Dropdown
                          fullWidth
                          name={`LocalizedMessages[${index}].Locale`}
                          label={t('user.locale')}
                          required
                          options={localeOptions.filter(
                            option =>
                              // Prevent selection of duplicate locale
                              !(values.LocalizedMessages[index].Locale !== option.value) ||
                              !values.LocalizedMessages.find(msg => msg.Locale === option.value)
                          )}
                        />
                      </Grid>
                      <Grid item xs>
                        <TextField
                          key={`LocalizedMessages[${index}].Title`}
                          name={`LocalizedMessages[${index}].Title`}
                          multiline
                          label={t('entities.systemmessage.title')}
                          required
                        />
                      </Grid>
                      <Grid item xs='auto'>
                        <Button variant='contained' onClick={() => remove(index)} startIcon={<DeleteIcon />}>
                          {t('global.remove')}
                        </Button>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          key={`LocalizedMessages[${index}].Message`}
                          name={`LocalizedMessages[${index}].Message`}
                          multiline
                          label={t('entities.systemmessage.message')}
                          required
                        />
                      </Grid>
                    </Grid>
                  ))}
                  <Grid item xs={12}>
                    <Button variant='contained' onClick={() => push({ Message: '', Title: '', Locale: '' })}>
                      {t('global.add')}
                    </Button>
                  </Grid>
                </Grid>
              )}
            </FieldArray>
          </Grid>
        </BasicFormLayout>
      )}
    </Form>
  )
}

export default MessageForm
